// SolyticsTooltip.jsx
// @ts-nocheck
/* eslint-disable react/prop-types */
import React from 'react';
import { Tooltip, Link, useTheme } from '@mui/material';
import SolyticsBox from './SolyticsBox';
import SolyticsTypography from './SolyticsTypography';
import SolyticsButton from './SolyticsButton';
import { ReactComponent as InfoOutlinedIcon } from '../../assets/Icons/lucideIcons/info.svg';

/**
 * SolyticsTooltip - A versatile tooltip component with multiple variants
 *
 * @component
 * @description
 * SolyticsTooltip is a wrapper around Material-UI's Tooltip component that provides three distinct variants:
 * - default: Simple text tooltip with custom styling
 * - guided: Step-by-step onboarding/tour style with navigation controls
 * - info: Informational tooltip with icon, title, and description
 *
 * @param {React.ReactNode} children - The element to wrap with the tooltip
 * @param {string} text - Text content for default tooltip (used with default variant)
 * @param {string} variant - The variant to use: 'default' | 'guided' | 'info' (default: 'default')
 * @param {string} title - Title text for guided and info variants
 * @param {string} subtitle - Subtitle or description text for guided and info variants
 * @param {string} placement - Tooltip placement: 'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'top-end' | etc. (default: 'top')
 * @param {boolean} arrow - If true, adds an arrow to the tooltip (default: true)
 * @param {boolean} open - If provided, controls the open state of the tooltip
 * @param {function} onClose - Callback fired when the tooltip requests to be closed
 * @param {number} currentStep - Current step number for guided variant (e.g., 1, 2, 3)
 * @param {number} totalSteps - Total number of steps for guided variant
 * @param {string} learnMoreLink - URL for the "Learn more" link in guided variant
 * @param {function} onSkip - Callback fired when "Skip" button is clicked in guided variant
 * @param {function} onNext - Callback fired when "Next" button is clicked in guided variant
 * @param {boolean} showIcon - If true, displays info icon in info variant (default: true)
 *
 * @returns {React.ReactElement} A tooltip element wrapping the children
 */
const SolyticsTooltip = ({
  children,
  //for default
  text,
  variant = 'default', // 'default' | 'guided' | 'info'
  title,
  subtitle,
  placement = 'top',
  arrow = true,
  open,
  onClose,
  // For guided variant
  currentStep,
  totalSteps,
  learnMoreLink,
  onSkip,
  onNext,
  // For info variant
  showIcon = true,
}) => {
  // Fallback: if no children provided, render text in a span
  const triggerElement = children || <span>{text}</span>;
  const theme = useTheme();

  // Variant: Default - simple text tooltip
  if (variant === 'default') {
    return (
      <Tooltip
        {...(text && { title: text })}
        {...(placement && { placement })}
        {...(arrow !== undefined && { arrow })}
        slotProps={{
          tooltip: {
            sx: {
              backgroundColor: theme.palette.primary.main,
              color: `${theme.palette.background.default} !important`,
              fontSize: '12px',
              fontWeight: 500,
              padding: '12px 16px',
              borderRadius: '8px',
              maxWidth: '360px',
              '& .MuiTooltip-arrow': {
                color: theme.palette.primary.main,
              },
            },
          },
        }}
      >
        {triggerElement}
      </Tooltip>
    );
  }

  // Variant: Guided - step-by-step onboarding/tour style
  if (variant === 'guided') {
    const guidedContent = (
      <SolyticsBox
        role="dialog"
        aria-modal="false"
        aria-labelledby={title ? 'guided-tooltip-title' : undefined}
        aria-describedby={subtitle ? 'guided-tooltip-description' : undefined}
        sx={{
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
          borderRadius: '12px',
          padding: '20px',
          maxWidth: '400px',
          boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
        }}
      >
        {/* Step indicator */}
        {currentStep && totalSteps && (
          <SolyticsBox sx={{ mb: 1 }}>
            <SolyticsTypography
              color={theme.palette.text.secondary}
              variant="captionMedium"
            >
              {currentStep}/{totalSteps}
            </SolyticsTypography>
          </SolyticsBox>
        )}

        {/* Title */}
        {title && (
          <SolyticsBox
            sx={{
              fontSize: '18px',
              fontWeight: 600,
              mb: 1,
            }}
          >
            <SolyticsTypography
              id="guided-tooltip-title"
              color={theme.palette.primary.main}
            >
              {title}
            </SolyticsTypography>
          </SolyticsBox>
        )}

        {/* Subtitle/Description */}
        {subtitle && (
          <SolyticsBox
            sx={{
              fontSize: '14px',
              fontWeight: 400,

              mb: 2,
            }}
          >
            <SolyticsTypography
              id="guided-tooltip-description"
              color={theme.palette.text.secondary}
            >
              {subtitle}
            </SolyticsTypography>
          </SolyticsBox>
        )}

        {/* Learn more link */}
        {learnMoreLink && (
          <Link
            href={learnMoreLink}
            target="_blank"
            rel="noopener noreferrer"
            underline="always"
            aria-label={`Learn more about ${title || 'this feature'}`}
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              color: theme.palette.primary.main,
              mb: 2,
              display: 'inline-block',
              cursor: 'pointer',
            }}
          >
            Learn more
          </Link>
        )}

        {/* Action buttons */}
        <SolyticsBox
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}
        >
          {onSkip && (
            <SolyticsButton variant="text" size="small" onClick={onSkip}>
              Skip
            </SolyticsButton>
          )}
          {onNext && (
            <SolyticsButton variant="contained" size="small" onClick={onNext}>
              Next
            </SolyticsButton>
          )}
        </SolyticsBox>
      </SolyticsBox>
    );

    return (
      <Tooltip
        title={guidedContent}
        {...(placement && { placement })}
        {...(arrow !== undefined && { arrow })}
        {...(open !== undefined && { open })}
        {...(onClose && { onClose })}
        slotProps={{
          tooltip: {
            sx: {
              backgroundColor: 'transparent',
              padding: 0,
              maxWidth: 'none',
            },
          },
        }}
      >
        {triggerElement}
      </Tooltip>
    );
  }

  // Variant: Info - informational tooltip with icon and title
  if (variant === 'info') {
    const infoContent = (
      <SolyticsBox
        sx={{
          display: 'flex',
          gap: 1.5,
          alignItems: 'flex-start',
        }}
      >
        {showIcon && (
          <InfoOutlinedIcon
            style={{
              height: '18px',
              width: '18px',
              color: theme.palette.text.secondary,
              mt: 0.2,
            }}
          />
        )}
        <SolyticsBox>
          {title && (
            <SolyticsBox
              sx={{
                mb: 0.5,
              }}
            >
              <SolyticsTypography
                color={theme.palette.text.primary}
                variant="h6"
              >
                {title}
              </SolyticsTypography>
            </SolyticsBox>
          )}
          {subtitle && (
            <SolyticsBox
              sx={{
                fontSize: '13px',
                fontWeight: 400,
              }}
            >
              <SolyticsTypography color={theme.palette.text.secondary}>
                {subtitle}
              </SolyticsTypography>
            </SolyticsBox>
          )}
        </SolyticsBox>
      </SolyticsBox>
    );

    return (
      <Tooltip
        title={infoContent}
        {...(placement && { placement })}
        {...(arrow !== undefined && { arrow })}
        slotProps={{
          tooltip: {
            sx: {
              backgroundColor: theme.palette.background.default,
              color: theme.palette.text.primary,
              fontSize: '13px',
              fontWeight: 400,
              padding: '12px 16px',
              borderRadius: '8px',
              maxWidth: '320px',
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
              '& .MuiTooltip-arrow': {
                color: theme.palette.background.default,
                '&::before': {
                  border: `1px solid ${theme.palette.divider}`,
                },
              },
            },
          },
        }}
      >
        {triggerElement}
      </Tooltip>
    );
  }

  return null;
};

export default SolyticsTooltip;
